import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import {
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useShiftNavigation } from "components/strategyDashboard/heats/context/ShiftNavigationProvider";
import dayjs, { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useState } from "react";
import { CraneShift } from "src/store/api/generatedApi";

const NOON_HOUR = 12;

// Determines if a shift is a "Day" shift by checking if it spans noon (12:00).
const isDayShift = (shift: CraneShift, localTimezone: string): boolean => {
  const startHour = dayjs(shift.shift_start_time).tz(localTimezone).hour();
  const endHour = dayjs(shift.shift_end_time).tz(localTimezone).hour();
  return startHour < NOON_HOUR && endHour >= NOON_HOUR;
};

export const ShiftStepper = () => {
  const shiftNav = useShiftNavigation();
  const { t } = useTenantTranslation();
  const { toLocalDatetime, localTimezone } = useLocalDatetime();
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  if (!shiftNav) return null;
  const {
    selectedShift,
    canGoBackward,
    canGoForward,
    goToPreviousShift,
    goToNextShift,
    goToDate,
    isLoading,
    isFetching,
  } = shiftNav;

  const formatShiftLabel = (shift: CraneShift): string => {
    const shiftDate = dayjs(shift.shift_start_time).tz(localTimezone);
    const currentYear = dayjs().tz(localTimezone).year();
    const dateFormat = shiftDate.year() < currentYear ? "MMM D, YYYY" : "MMM D";
    const date = toLocalDatetime(shift.shift_start_time, dateFormat);
    const startTime = toLocalDatetime(shift.shift_start_time, "LT");
    const endTime = toLocalDatetime(shift.shift_end_time, "LT");
    return `${date} · ${startTime}–${endTime}`;
  };

  const getShiftChipLabel = (shift: CraneShift): string => {
    const isDay = isDayShift(shift, localTimezone);
    const icon = isDay ? "☀️ Day" : "🌙 Night";
    if (shift.crew_id) {
      return `${icon} • ${shift.crew_id} ${t("crew")}`;
    }
    return icon;
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      void goToDate(date.startOf("day"));
    }
    setDatePickerOpen(false);
  };

  const selectedDate = selectedShift
    ? dayjs(selectedShift.shift_start_time).tz(localTimezone)
    : null;

  const todayAtLocalTimezone = dayjs().tz(localTimezone).endOf("day");

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <IconButton
        onClick={goToPreviousShift}
        disabled={!canGoBackward || isLoading}
      >
        {!canGoBackward && isFetching && !isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <ArrowBackIosNewRoundedIcon />
        )}
      </IconButton>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ minWidth: 200, justifyContent: "center" }}
      >
        {isLoading ? (
          <CircularProgress size={16} />
        ) : selectedShift ? (
          <>
            <Typography variant="body1" sx={{ fontWeight: "fontWeightMedium" }}>
              {formatShiftLabel(selectedShift)}
            </Typography>
            <Chip
              label={getShiftChipLabel(selectedShift)}
              size="small"
              variant="outlined"
              color="primary"
            />
          </>
        ) : isFetching ? (
          <CircularProgress size={16} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("noShiftsAvailable")}
          </Typography>
        )}
      </Stack>

      <IconButton onClick={goToNextShift} disabled={!canGoForward || isLoading}>
        <ArrowForwardIosRoundedIcon />
      </IconButton>

      <IconButton
        onClick={() => {
          setDatePickerOpen(true);
        }}
        disabled={isLoading}
      >
        <CalendarMonthRoundedIcon />
      </IconButton>
      <DatePicker
        open={datePickerOpen}
        onClose={() => {
          setDatePickerOpen(false);
        }}
        value={selectedDate}
        onChange={handleDateChange}
        shouldDisableDate={(dateToEvaluate) =>
          dateToEvaluate.tz(localTimezone).isAfter(todayAtLocalTimezone, "day")
        }
        slotProps={{
          textField: {
            sx: { display: "none" },
          },
        }}
      />
    </Stack>
  );
};
